import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMFOItems } from "@/hooks/use-mfo-items";
import { useDepartments } from "@/hooks/use-departments";
import { MFOItem } from "@/types/kanban";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  Users,
  Building2,
  FileText,
  Edit,
  Share2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getPriorityColor } from "@/utils/statusPriorityUtils";
import AddEmployeeModal from "@/components/employee/AddEmployeeModal";
import { useGetEmployees, useCreateEmployee } from "@/hooks/use-employees";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PublicAccessLink from "@/components/mfo/PublicAccessLink";
import { supabase } from "@/integrations/supabase/client";

const MFODetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getMFODetail, updateSubtaskCompletion } = useMFOItems();
  const { departments } = useDepartments();
  const [mfo, setMfo] = useState<MFOItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { mutate: createEmployee, isPending } = useCreateEmployee();
  const { data: employees = [] } = useGetEmployees();
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isPublicAccessModalOpen, setIsPublicAccessModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await getMFODetail(id);
      setMfo(data);
    } catch (error) {
      console.error("Error fetching MFO detail:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, getMFODetail]);

  useEffect(() => {
    fetchData();

    // Set up real-time subscription for MFO progress updates
    if (id) {
      const mfoProgressSubscription = supabase
        .channel(`mfo_progress:${id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'mfo_items',
            filter: `id=eq.${id}`
          },
          (payload) => {
            // Update the MFO progress in the local state
            setMfo(prev => {
              if (!prev) return null;
              return {
                ...prev,
                progress: payload.new.progress
              };
            });
          }
        )
        .subscribe();

      return () => {
        mfoProgressSubscription.unsubscribe();
      };
    }
  }, [id, fetchData]);

  const handleSubtaskToggle = async (subtaskId: string, completed: boolean) => {
    if (!id) return;
    try {
      const success = await updateSubtaskCompletion(id, subtaskId, completed);
      if (success && mfo) {
        const updatedSubTasks = mfo.subTasks?.map((task) =>
          task.id === subtaskId ? { ...task, completed } : task
        );
        setMfo({ ...mfo, subTasks: updatedSubTasks });
      }
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const handleAddEmployee = (employee: {
    name: string;
    email: string;
    position: string;
    phone: string;
    status: string;
    start_date: string;
    department_id?: string;
  }) => {
    createEmployee({
      name: employee.name,
      email: employee.email,
      position: employee.position,
      departmentId: employee.department_id || '',
      phone: employee.phone,
      startDate: employee.start_date,
      status: employee.status
    }, {
      onSuccess: () => {
        toast({
          title: "Employee added",
          description: `Successfully added ${employee.name} to the system`,
        });
        setIsEmployeeModalOpen(false);
      },
      onError: (error: any) => {
        toast({
          title: "Error adding employee",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  if (!mfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-xl mb-4">MFO not found</p>
        <Button onClick={() => navigate("/dashboard/mfo")}>
          Return to MFO List
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mr-2"
          onClick={() => navigate("/dashboard/mfo")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to MFO List
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">
            {mfo.code}: {mfo.title}
          </h1>
          <p className="text-muted-foreground">
            {mfo.departmentName || "Department not specified"}
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/mfo`)}>
            <FileText className="h-4 w-4 mr-2" />
            All MFOs
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/mfo/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit MFO
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsPublicAccessModalOpen(true)}>
            <Share2 className="h-4 w-4 mr-2" />
            Share Publicly
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge
              className={`text-xs font-semibold uppercase ${getPriorityColor(
                mfo.priority
              )}`}
            >
              {mfo.priority}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center">
            {mfo.status === "completed" && (
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
            )}
            {mfo.status === "in-progress" && (
              <Clock className="h-4 w-4 mr-2 text-blue-500" />
            )}
            {mfo.status === "not-started" && (
              <Clock className="h-4 w-4 mr-2 text-gray-500" />
            )}
            {mfo.status === "delayed" && (
              <Clock className="h-4 w-4 mr-2 text-red-500" />
            )}
            <span
              className={`capitalize ${
                mfo.status === "completed"
                  ? "text-green-700"
                  : mfo.status === "in-progress"
                  ? "text-blue-700"
                  : mfo.status === "delayed"
                  ? "text-red-700"
                  : "text-gray-700"
              }`}
            >
              {mfo.status.replace("-", " ")}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Due Date
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            <span>
              {new Date(mfo.dueDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </CardContent>
        </Card>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Progress</h2>
        <div className="flex items-center">
          <Progress value={mfo.progress} className="h-2 flex-1 mr-4" />
          <span className="text-sm font-medium">{mfo.progress}%</span>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Tasks
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            Team
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Success Indicators</CardTitle>
              <CardDescription>
                Key performance indicators for this MFO
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {mfo.successIndicators?.length ? (
                mfo.successIndicators.map((indicator, index) => (
                  <div
                    key={index}
                    className="p-3 border rounded-lg bg-accent/10"
                  >
                    {indicator}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">
                  No success indicators defined
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Budget</CardTitle>
              <CardDescription>
                Allocated budget for this MFO item
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                ₱{mfo.budget.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
              <CardDescription>
                Timeline for implementation across quarters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q1
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q1
                </div>
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q2
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q2
                </div>
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q3
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q3
                </div>
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q4
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q4
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between">
                <CardTitle>Department</CardTitle>
                <Badge variant="outline">Division</Badge>
              </div>
            </CardHeader>
            <CardContent className="flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              <span>
                {mfo.departmentName || "Department not assigned"}
              </span>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sub-Tasks</CardTitle>
              <CardDescription>
                Track sub-tasks completion for this MFO
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mfo.subTasks && mfo.subTasks.length > 0 ? (
                  mfo.subTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center p-2 border rounded-lg"
                    >
                      <input
                        type="checkbox"
                        id={task.id}
                        checked={task.completed}
                        onChange={(e) =>
                          handleSubtaskToggle(task.id, e.target.checked)
                        }
                        className="h-4 w-4 mr-3 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <label
                        htmlFor={task.id}
                        className={`flex-1 ${
                          task.completed ? "line-through text-muted-foreground" : ""
                        }`}
                      >
                        {task.text}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground">No sub-tasks defined</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>
                  Personnel assigned to this MFO
                </CardDescription>
              </div>
              <Button 
                size="sm" 
                onClick={() => setIsEmployeeModalOpen(true)}
                variant="outline"
              >
                Add Employee
              </Button>
            </CardHeader>
            <CardContent>
              {mfo.collaborators && mfo.collaborators.length > 0 ? (
                <div className="space-y-3">
                  {mfo.collaborators.map((person) => (
                    <div
                      key={person.id}
                      className="flex items-center p-3 border rounded-lg"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-3">
                        {person.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <p className="font-medium">{person.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No team members assigned yet
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AddEmployeeModal
        isOpen={isEmployeeModalOpen}
        onOpenChange={setIsEmployeeModalOpen}
        onSave={handleAddEmployee}
        departments={departments}
      />

      <Dialog open={isPublicAccessModalOpen} onOpenChange={setIsPublicAccessModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Public Access Link</DialogTitle>
            <DialogDescription>
              Generate a public link to share this MFO with team members who don't have system accounts.
            </DialogDescription>
          </DialogHeader>
          <PublicAccessLink
            mfoId={id}
            publicAccessToken={mfo.publicAccessToken}
            onTokenGenerated={(token) => {
              setMfo({ ...mfo, publicAccessToken: token });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MFODetail;
